import React, { useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import DeckGL from 'deck.gl';
import type { DeckGLRef, PickingInfo } from 'deck.gl';
import { Map, Popup } from 'react-map-gl';
import type { MapRef } from 'react-map-gl';
import '@/assets/css/content.css';
import DashboardContext from '@/contexts/DashboardContext';
import './Map.scss';
import { useDigitalTwinsContext } from '@/contexts/DigitalTwinsContext';
import type { RootState } from '@/redux/store';
import type { Infrastructure } from './mapModes/MapModeStrategy';
import { useRadarLayer, useRadarControls, RadarLoadingOverlay, RadarTimestampBadge } from '@/features/radar';
import { SensorDeviceModal } from '@/features/sensorMaintenanceLens';
import MapTooltip from './MapTooltip';
import type { UseMmsMapDataResult } from './hooks/useMmsMapData';
import { useMmsInteractions } from './hooks/useMmsInteractions';
import { useMapViewState } from './hooks/useMapViewState';
import type { SelectedMap } from './hooks/useMapViewState';
import { useMapHover } from './hooks/useMapHover';
import { useMapPopup } from './hooks/useMapPopup';
import { useDashboardMapEvents } from './hooks/useDashboardMapEvents';
import { useFlowAnimation } from './hooks/useFlowAnimation';
import { useGisMapData } from './hooks/useGisMapData';
import { useGisLayers, getGisTooltip } from './hooks/useGisLayers';
import { useFlowMapData } from './hooks/useFlowMapData';
import { useMapDataLoading } from './hooks/useMapDataLoading';
import { useDmaOverlay } from './hooks/useDmaOverlay';
import { useMapModeStrategy } from './hooks/useMapModeStrategy';
import { useInfrastructureIconLayers } from './hooks/useInfrastructureIconLayers';
import { usePulseHighlight } from './hooks/usePulseHighlight';
import { useSensorMaintenanceOverlay } from './hooks/useSensorMaintenanceOverlay';
import { useDigitalTwinNetworkOverlay } from './hooks/useDigitalTwinNetworkOverlay';
import { useSewerAnalysisMode } from './hooks/useSewerAnalysisMode';
import StrategyPopup from './components/StrategyPopup';
import MapTooltipContent from './components/MapTooltipContent';

interface MapStateProps {
	selectedMap: SelectedMap;
	mapTheme: string;
}

interface OwnProps {
	mmsMapData: UseMmsMapDataResult;
}

const mapboxApiAccessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

const MainMap: React.FC<MapStateProps & OwnProps> = ({ selectedMap, mapTheme, mmsMapData }) => {
	const { value: contextValue } = useContext(DashboardContext);
	const digitalTwinsContext = useDigitalTwinsContext();
	const { showGisLayer, selectedDigitalTwinId, selectedDigitalTwinsData } = digitalTwinsContext;

	const deckRef = useRef<DeckGLRef | null>(null);
	const mapRef = useRef<MapRef>(null);

	const { viewState, setViewState, applyViewStateChange } = useMapViewState(selectedMap);
	const popup = useMapPopup(mapRef);
	const {
		showPopup,
		infraInfo,
		popupCoordinates,
		popupAdditionalData,
		isPopupDragging,
		openPopup,
		closePopup,
		hidePopup,
		handlePopupDragMouseDown,
	} = popup;
	const { hoverInfo, clearHover, handleIconHover, handleDmaHover, handleSensorHover } = useMapHover();

	const gisData = useGisMapData(Number(contextValue.selectedLocation) || null);
	const flowLines = useFlowMapData(Number(contextValue.selectedLocation) || null);
	const showLinks = !contextValue.activatedDigitalTwinMode || showGisLayer;
	const { layers: gisLayers } = useGisLayers({
		nodes: gisData.nodes,
		lines: gisData.lines,
		zoom: viewState.zoom,
		enabled: !contextValue.activatedMmsMode,
		visible: showLinks,
	});
	const { tripLayers, changeAnimate } = useFlowAnimation(flowLines);

	const infras = useSelector((state: RootState): Infrastructure[] => state.dashboard.infrastructure);
	const selectedInfrastructureTypes = useSelector((state: RootState): string[] => state.dashboard.selectedInfraTypes);
	const { radarEnabled, radarFuture, enableRadar, disableRadar, setRadarFuture } = useRadarControls();

	const {
		isDmaOverlayEnabled,
		dmaLayers,
		dmaPanel,
		dmaOnMapClick,
		onSetMode,
		onResetDma,
	} = useDmaOverlay({
		selectedLocation: contextValue.selectedLocation,
		selectedDate: contextValue.selectedDate,
		onHover: handleDmaHover,
	});

	useMapDataLoading({ contextValue, isDmaOverlayEnabled, onModeChange: hidePopup });

	const { layers: radarLayers, radarState, currentTimestamp: radarCurrentTimestamp, retry: retryRadar } =
		useRadarLayer({
			enabled: radarEnabled,
			isFuture: radarFuture,
		});

	const {
		mmsLayers,
		isMmsDraggingNode,
		isDraggingNodeRef: mmsIsDraggingNodeRef,
		handlePointerDown: handleMmsPointerDown,
		handlePointerUp: handleMmsPointerUp,
		handleDragStart: mmsHandleDragStart,
		handleDrag: mmsHandleDrag,
		handleDragEnd: mmsHandleDragEnd,
		handleMmsMapClick,
		getMmsTooltip,
	} = useMmsInteractions({
		mmsMapData,
		activatedMmsMode: !!contextValue.activatedMmsMode,
		selectedMmsId: contextValue.selectedMmsId,
		viewState,
		setViewState,
		mapRef,
		openPopup,
		hidePopup,
	});

	const { strategy, mapContext, digitalTwinContextForStrategy, handleAssetClick } = useMapModeStrategy({
		contextValue,
		digitalTwinsContext,
		isDmaOverlayEnabled,
		popup,
	});

	const { iconLayers, iconOpacities, getExpansionZoom } = useInfrastructureIconLayers({
		infras,
		selectedInfrastructureTypes,
		selectedSensors: contextValue.selectedSensors,
		strategy,
		mapContext,
		digitalTwinContext: digitalTwinContextForStrategy,
		zoom: viewState.zoom,
		onIconHover: handleIconHover,
	});

	const { pulseLayers, zoomToAsset } = usePulseHighlight({
		infras,
		zoom: viewState.zoom,
		setViewState,
	});

	const {
		sensorLayers,
		sensorOnMapClick,
		sensorDevicePopup,
		closeSensorDevicePopup,
	} = useSensorMaintenanceOverlay({
		enabled: !!contextValue.activatedSensorMaintenanceMode,
		zoom: viewState.zoom,
		selectedLocation: contextValue.selectedLocation,
		setViewState,
		onDeviceHover: handleSensorHover,
		onDeviceClick: clearHover,
	});

	const { dtNetworkLayers } = useDigitalTwinNetworkOverlay({
		enabled: !!contextValue.activatedDigitalTwinMode,
		selectedDigitalTwinId,
		selectedDigitalTwinsData,
		setViewState,
	});

	const {
		layers: sewerAnalysisLayers,
		panelElement: sewerAnalysisPanel,
		onMapClick: sewerAnalysisOnMapClick,
	} = useSewerAnalysisMode({
		enabled: !!contextValue.activatedSewerAnalysisMode,
	});

	useEffect(() => {
		if (showPopup && infraInfo) {
			const assetId = infraInfo.measurementeInfraestructureId;
			if (iconOpacities[assetId] === 0) {
				closePopup();
			}
		}
		if (hoverInfo?.object && hoverInfo.object._tooltipType !== 'sensor') {
			const assetId = hoverInfo.object.measurementeInfraestructureId;
			if (assetId === undefined) return;

			if (iconOpacities[assetId] === 0) {
				clearHover();
			}
		}
	}, [iconOpacities]);

	const handleClickMap = useCallback((info: PickingInfo) => {
		clearHover();

		if (sensorOnMapClick(info)) return;
		closeSensorDevicePopup();

		if (dmaOnMapClick(info)) return;
		if (sewerAnalysisOnMapClick(info)) return;

		if (info.picked && info.object?._isCluster) {
			const clusterId = info.object.clusterId;
			const expansionZoom = getExpansionZoom(clusterId);
			setViewState((prev) => ({
				...prev,
				longitude: info.object.longitude,
				latitude: info.object.latitude,
				zoom: Math.min(expansionZoom + 1, 20),
			}));
			return;
		}

		if (handleMmsMapClick(info)) return;

		if (info.picked && info.object?.measurementeInfraestructureId) {
			handleAssetClick(info.object);
		} else {
			hidePopup();
		}
	}, [handleAssetClick, getExpansionZoom, dmaOnMapClick, sewerAnalysisOnMapClick, handleMmsMapClick, sensorOnMapClick, closeSensorDevicePopup]);

	useDashboardMapEvents({
		onChangeAnimation: changeAnimate,
		onSetMode,
		onResetDma,
		onCleanRainfallLayer: disableRadar,
		onDrawRainfallLayer: enableRadar,
		onFutureRainfallToggleChanged: setRadarFuture,
		onClosePopup: closePopup,
		onZoomToAsset: zoomToAsset,
	});

	const activeLayers = useMemo(() => {
		if (contextValue.activatedMmsMode) {
			return [...mmsLayers, ...radarLayers];
		}
		if (contextValue.activatedSensorMaintenanceMode) {
			return [...gisLayers, ...sensorLayers, ...radarLayers];
		}

		return [
			...gisLayers,
			...(contextValue.activatedDigitalTwinMode ? dtNetworkLayers : []),
			...iconLayers,
			...pulseLayers,
			...tripLayers,
			...radarLayers,
			...dmaLayers,
			...sewerAnalysisLayers,
		];
	}, [
		contextValue.activatedMmsMode,
		contextValue.activatedSensorMaintenanceMode,
		contextValue.activatedDigitalTwinMode,
		gisLayers,
		dtNetworkLayers,
		iconLayers,
		pulseLayers,
		mmsLayers,
		sensorLayers,
		tripLayers,
		radarLayers,
		dmaLayers,
		sewerAnalysisLayers,
	]);

	return (
		<div
			className="MapContainer"
			style={{
				height: 'calc(100vh - 80px)',
				width: '100%',
				position: 'absolute',
				backgroundColor: (mapTheme?.includes('dark') || mapTheme?.includes('satellite'))
					? '#191a1a'
					: '#f0ece8',
			}}
			onPointerDown={handleMmsPointerDown}
			onPointerUp={handleMmsPointerUp}
		>
			<>
				<Map
					ref={mapRef}
					mapboxAccessToken={mapboxApiAccessToken}
					mapStyle={mapTheme}
					{...viewState}
				>
					<DeckGL
						ref={deckRef}
						viewState={viewState}
						layers={activeLayers}
						controller={{
							scrollZoom: true,
							dragPan: !isMmsDraggingNode,
							dragRotate: strategy.dragRotate,
							doubleClickZoom: true,
							touchZoom: true,
							touchRotate: strategy.touchRotate,
							keyboard: true,
							inertia: true,
						}}
						style={{
							position: 'relative',
							width: '100%',
							height: '100%',
						}}
						onClick={handleClickMap}
						onDragStart={contextValue.activatedMmsMode ? mmsHandleDragStart : undefined}
						onDrag={contextValue.activatedMmsMode ? mmsHandleDrag : undefined}
						onDragEnd={contextValue.activatedMmsMode ? mmsHandleDragEnd : undefined}
						onViewStateChange={({ viewState: nextViewState }) => {
							if (mmsIsDraggingNodeRef.current) return;
							applyViewStateChange(nextViewState);
						}}
						getCursor={({ isHovering, isDragging }) =>
							isMmsDraggingNode || isDragging ? 'grabbing' : isHovering ? 'pointer' : 'grab'
						}
						getTooltip={contextValue.activatedMmsMode
							? getMmsTooltip
							: (info: PickingInfo) => getGisTooltip(info, viewState.zoom)}
					>
						{showPopup && infraInfo && (
							<>
								<Popup
									longitude={popupCoordinates[0]}
									latitude={popupCoordinates[1]}
									onClose={hidePopup}
									closeButton={false}
									closeOnClick={false}
									className="rounded-lg"
									style={{ maxWidth: '100%', width: 'auto', borderRadius: '30px' }}
								>
									<StrategyPopup
										strategy={strategy}
										infraInfo={infraInfo}
										mapContext={mapContext}
										popupAdditionalData={popupAdditionalData}
										onClose={hidePopup}
										onMouseDown={handlePopupDragMouseDown}
										isDragging={isPopupDragging}
									/>
								</Popup>
							</>
						)}
						{sensorDevicePopup &&
							Number.isFinite(sensorDevicePopup.longitude) &&
							Number.isFinite(sensorDevicePopup.latitude) && (
								<Popup
									longitude={sensorDevicePopup.longitude}
									latitude={sensorDevicePopup.latitude}
									onClose={closeSensorDevicePopup}
									closeButton={false}
									closeOnClick={false}
									className="rounded-lg"
									style={{ maxWidth: '100%', width: 'auto', borderRadius: '30px', zIndex: 9999 }}
								>
									<SensorDeviceModal
										device={sensorDevicePopup}
										onClose={closeSensorDevicePopup}
									/>
								</Popup>
							)}
						{dmaPanel}
						{sewerAnalysisPanel}
					</DeckGL>
				</Map>

				{radarEnabled && (
					<RadarLoadingOverlay radarState={radarState} onRetry={retryRadar} />
				)}

				{radarCurrentTimestamp && radarState.status === 'loaded' && (
					<RadarTimestampBadge timestamp={radarCurrentTimestamp} />
				)}

				{hoverInfo &&
					hoverInfo.object &&
					!(hoverInfo.object._tooltipType === 'sensor' && sensorDevicePopup) && (
						<MapTooltip
							x={hoverInfo.x}
							y={hoverInfo.y}
							iconSize={Math.max(10, 25 * (viewState.zoom / 10))}
						>
							<MapTooltipContent infra={hoverInfo.object} />
						</MapTooltip>
					)}
			</>
		</div>
	);
};

function mapState(state: RootState): MapStateProps {
	return {
		selectedMap: state.maps.array,
		mapTheme: state.organization?.settings?.mapSettings?.settings?.style,
	};
}

export default connect(mapState)(MainMap);
